import * as React from 'react';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import type { FieldFormProps } from '@helpcenter/field-types';
import type { showErrorFlag } from '@helpcenter/flags';
import { FormMultiSelect } from '@helpcenter/form-select';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import type { MultiServicePickerOption } from 'rest/service-picker';
import type { ValueType } from '@atlaskit/select';
import { toMultiSelectValue } from '@atlassian/help-center-common-util/select';
import messages from './messages';
import { usePaginatedServiceOptions } from './use-paginated-service-options';

const MultiServicePicker = (
    props: FieldFormProps<string[]> & WrappedComponentProps & { showErrorFlag: typeof showErrorFlag }
) => {
    const { disabled, onChange, intl, ...otherProps } = props;
    const { useCallback } = React;

    const noOptionsMessage = useCallback(() => {
        return intl.formatMessage(messages.noOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { options, fetchMoreOptions, ...paginatedMultiSelectProps } = usePaginatedServiceOptions(props.showErrorFlag);

    const onChangeForMultiSelect = useCallback(
        (opts: ValueType<MultiServicePickerOption>) => {
            const multiSelectValue = toMultiSelectValue(opts);
            onChange?.(
                props.id,
                multiSelectValue.map((v) => v.value)
            );

            // search again if all the options have been selected
            if (multiSelectValue.length === options.length) {
                fetchMoreOptions();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onChange]
    );

    return (
        <FormMultiSelect
            {...otherProps}
            {...paginatedMultiSelectProps}
            actionSubjectId="multiServicePickerField"
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            noOptionsMessage={noOptionsMessage}
            isDisabled={disabled}
            onMenuScrollToBottom={fetchMoreOptions}
            options={options}
            onChange={onChangeForMultiSelect}
        />
    );
};

const WithErrorBoundary = (
    props: FieldFormProps<string[]> & WrappedComponentProps & { showErrorFlag: typeof showErrorFlag }
) => (
    <ErrorBoundary id="multi-service-picker" packageName="portal_fields">
        <MultiServicePicker {...props} />
    </ErrorBoundary>
);

export default injectIntl(WithErrorBoundary);
